import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import type { JSX } from "react";
import { Link as WouterLink } from "wouter";
import { ROUTE } from "../../common/ROUTE";

interface MdaiLinksProps {
  label?: string | JSX.Element;
  href: string;
  color?: string;
  buttonColor?:
    | "inherit"
    | "primary"
    | "secondary"
    | "success"
    | "error"
    | "info"
    | "warning";
  externalIcons?: boolean;
  style?: React.CSSProperties;
  type?: "button" | "link";
  variant?: "text" | "outlined" | "contained";
  size?: "small" | "medium" | "large";
  width?: { sm: string; md: string; lg: string };
  aria?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
}

export const MdaiLink = ({
  label,
  href,
  color = "textPrimary",
  buttonColor = "primary",
  externalIcons = true,
  style,
  type = "link",
  variant = "contained",
  size = "large",
  width,
  aria,
  children,
  disabled = false,
  onClick,
}: MdaiLinksProps) => {
  const isExternal =
    typeof href === "string" &&
    (href.startsWith("https://") || href.startsWith("mailto:"));
  const isHashLink = href.includes("#");
  return (
    <>
      {isExternal || href === ROUTE.CONTACT || isHashLink ? (
        type === "button" ? (
          <Button
            disabled={disabled}
            variant={variant}
            color={buttonColor}
            size={size}
            sx={{
              display: "flex",
              alignItems: "center",
              width: {
                sm: width?.sm ?? "auto",
                md: width?.md ?? "auto",
                lg: width?.lg ?? "auto",
              },
              ...style,
            }}
            href={href}
            aria-label={aria}
            {...(isExternal && {
              target: "_blank",
              rel: "noopener noreferrer",
            })}
            onClick={onClick}
          >
            {children ?? label}
          </Button>
        ) : (
          <Link
            variant="body1"
            aria-label={aria}
            color={color}
            href={href}
            gap={1}
            sx={
              children
                ? {
                    textDecoration: "none",
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    height: "100%",
                    ...style,
                  }
                : { display: "flex", alignItems: "center", ...style }
            }
            {...(isExternal && {
              target: "_blank",
              rel: "noopener noreferrer",
            })}
            onClick={disabled ? undefined : onClick}
          >
            {children ?? (
              <>
                {label}
                {externalIcons && isExternal && <ArrowOutwardRoundedIcon />}
              </>
            )}
          </Link>
        )
      ) : (
        <WouterLink
          to={disabled ? "" : href}
          aria-label={aria}
          style={
            children
              ? {
                  textDecoration: "none",
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  height: "100%",
                  ...(style ?? {}),
                }
              : style
          }
        >
          {type === "button" ? (
            <Button
              variant={variant}
              size={size}
              color={buttonColor}
              sx={{
                display: "flex",
                alignItems: "center",
                width: {
                  sm: width?.sm ?? "auto",
                  md: width?.md ?? "auto",
                  lg: width?.lg ?? "auto",
                },
                ...style,
              }}
              disabled={disabled}
              onClick={onClick}
            >
              {children ?? label}
            </Button>
          ) : children ? (
            <>{children}</>
          ) : (
            <Typography
              variant="body1"
              color={color}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                ...style,
              }}
              onClick={disabled ? undefined : onClick}
            >
              {label}
            </Typography>
          )}
        </WouterLink>
      )}
    </>
  );
};
